import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaSearchDollar, FaMousePointer, FaBullseye, FaFilter, FaChartLine, FaPercentage,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaGoogle, FaFacebook, FaLinkedin, FaInstagram,
    FaAd, FaShoppingCart, FaGlobe, FaMobileAlt
} from 'react-icons/fa';
import heroImage from '../../assets/1e9530694cfcc1d368a95d2b3056f69d.jpg';

const PPCAdvertisingPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaGoogle, title: 'Google Search Ads', desc: 'Capture high-intent leads exactly when they search for your services.', image: 'https://images.unsplash.com/photo-1557838923-2985c318be48?w=600' },
        { icon: FaFacebook, title: 'Social Advertising', desc: 'Target specific demographics on Facebook, Instagram, and LinkedIn.', image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600' },
        { icon: FaShoppingCart, title: 'Google Shopping', desc: 'Showcase your products directly in search results with visual ads.', image: 'https://images.unsplash.com/photo-1472851294608-4155f2118c03?w=600' },
        { icon: FaFilter, title: 'Retargeting', desc: 'Re-engage opportunities by showing ads to visitors who left your site.', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600' },
        { icon: FaAd, title: 'Display Advertising', desc: 'Build brand awareness with visual banners across millions of websites.', image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600' },
        { icon: FaChartLine, title: 'Conversion Rate Opt.', desc: 'Tweaking landing pages to ensure every click has the best chance to convert.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' }
    ];

    const platforms = [
        { name: 'Google Ads', icon: FaGoogle, desc: 'Search & Display' },
        { name: 'Meta Ads', icon: FaFacebook, desc: 'Facebook & Insta' },
        { name: 'LinkedIn', icon: FaLinkedin, desc: 'B2B Targeting' },
        { name: 'YouTube', icon: FaGoogle, desc: 'Video Ads' },
        { name: 'Retargeting', icon: FaMousePointer, desc: 'Audience Recovery' },
        { name: 'Display', icon: FaGlobe, desc: 'Banner Networks' },
        { name: 'Analytics', icon: FaChartLine, desc: 'ROI Tracking' },
        { name: 'Mobile', icon: FaMobileAlt, desc: 'App Installs' }
    ];

    // Horizontal Flow Steps (PPC Launchpad)
    const flowSteps = [
        { title: 'Audit', icon: FaSearchDollar, color: 'from-blue-500 to-cyan-500' },
        { title: 'Strategy', icon: FaBullseye, color: 'from-indigo-500 to-purple-500' },
        { title: 'Setup', icon: FaFilter, color: 'from-teal-400 to-emerald-500' },
        { title: 'Launch', icon: FaMousePointer, color: 'from-orange-400 to-red-500' },
        { title: 'Optimize', icon: FaChartLine, color: 'from-green-500 to-lime-500' },
        { title: 'Scale', icon: FaPercentage, color: 'from-pink-500 to-rose-500' }
    ];

    const faqs = [
        { q: 'How much does PPC cost?', a: 'You set the budget. We recommend a minimum starting budget to gather enough data for optimization, but you only pay when someone clicks your ad.' },
        { q: 'Google Ads or Facebook Ads?', a: 'Google Ads capture "intent" (people searching for you). Facebook Ads generate "demand" (targeting people based on interests). We often use both.' },
        { q: 'How fast will I see results?', a: 'Instantly. Unlike SEO, PPC ads can drive traffic the moment the campaign is launched.' },
        { q: 'What is ROAS?', a: 'Return On Ad Spend. It measures how much revenue you get for every dollar spent on ads. Our goal is always a positive, scalable ROAS.' },
        { q: 'Do you design the ad creatives?', a: 'Yes, our team handles copywriting and basic banner design. For video ads, we can collaborate with your assets or our creative team.' }
    ];

    const whyChooseUs = [
        { title: 'Data-Driven Bidding', desc: 'We don\'t guess. We use real-time data to bid exactly what a click is worth.' },
        { title: 'Negative Keywords', desc: 'We rigorously block irrelevant search terms so you don\'t waste money.' },
        { title: 'Landing Page Focus', desc: 'Traffic is useless if it doesn\'t convert. We advise on page optimization.' },
        { title: 'Transparent Fees', desc: 'No hidden markups on media spend. You pay the platforms directly.' },
        { title: 'A/B Testing', desc: 'We constantly test headlines, images, and offers to beat the control.' },
        { title: 'B2B Specialist', desc: 'We know how to target decision-makers, not just generic traffic.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-cyan-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-cyan-300 mb-3">What We Do &gt; Performance</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">PPC Advertising</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Instant traffic, measurable ROI. We turn ad spend into profit.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-cyan-700 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Free Audit
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-cyan-700 transition-all text-sm whitespace-nowrap">
                                Our Platforms
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Pay for Results, Not Promises</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Precision targeting that puts your brand in front of ready-to-buy customers.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8 text-center divide-x divide-slate-200">
                        {[
                            { label: 'Ad Spend Managed', val: '$500k+' },
                            { label: 'Avg ROAS', val: '4.5x' },
                            { label: 'Leads Generated', val: '15k+' },
                            { label: 'Cost Reduction', val: '-30%' }
                        ].map((stat, idx) => (
                            <div key={idx} className="p-4">
                                <div className="text-4xl font-bold text-cyan-600 mb-2">{stat.val}</div>
                                <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies / Platforms */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Ad Platforms We Master</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Reaching your audience wherever they are online.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {platforms.map((plat, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <plat.icon className="text-5xl mx-auto mb-4 text-cyan-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{plat.name}</h3>
                                <p className="text-sm text-slate-600">{plat.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">PPC Services</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Comprehensive campaign management for maximum returns.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4 text-cyan-300" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-cyan-600 to-blue-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">The Tech-Inovant Advantage</h2>
                        <p className="text-lg text-cyan-100 max-w-3xl mx-auto">
                            We treat your budget like it's our own.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-cyan-300 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-cyan-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Campaign Roadmap with Arrows) */}
            <section className="py-24 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">PPC Launchpad</h2>
                        <p className="text-slate-600">Our proven process for profitable campaigns.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 md:rounded-full -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">

                                    {/* ARROW: Only show BETWEEN steps */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="hidden md:flex absolute top-[30px] -right-[50%] w-full justify-center items-center z-10 pointer-events-none">
                                            <motion.div
                                                animate={{ x: [-3, 3, -3] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <FaChevronRight className="text-2xl text-slate-400 font-bold" />
                                            </motion.div>
                                        </div>
                                    )}

                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white ring-4 ring-slate-50`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md z-30">
                                        {idx + 1}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-cyan-600 transition-colors mt-2">{step.title}</h3>

                                    {/* Mobile Vertical Line */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute bottom-[-40px] left-1/2 w-0.5 h-[40px] bg-slate-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">PPC FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-cyan-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Estimate Your ROI</h2>
                            <p className="text-slate-600 mb-6">Get a free campaign forecast today.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Website URL</label>
                                    <input type="url" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-cyan-600" placeholder="https://yourwebsite.com" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Budget</label>
                                        <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-cyan-600">
                                            <option>Under $1,000</option>
                                            <option>$1,000 - $5,000</option>
                                            <option>$5,000 - $10,000</option>
                                            <option>$10,000+</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                        <input type="tel" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-cyan-600" placeholder="+91 98765 43210" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Goal</label>
                                    <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-cyan-600">
                                        <option>Lead Generation</option>
                                        <option>E-commerce Sales</option>
                                        <option>Brand Awareness</option>
                                        <option>App Installs</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-cyan-600 text-white font-medium py-3 rounded-sm hover:bg-cyan-700 transition-colors shadow-lg">
                                    Get Media Plan
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-cyan-700 to-blue-800 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Ready to Scale?</h2>
                    <p className="text-xl font-light mb-8 text-cyan-100">
                        Stop waiting for organic growth. Accelerate your results today.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-cyan-800 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Launch Campaign
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-cyan-800 transition-all text-sm whitespace-nowrap">
                            Contact Strategist
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PPCAdvertisingPage;
